// src/myclipsv3/ui/CookieConsentBanner.js
//
// FAZ 6 GELİŞTİRME 2 + RIZA DENETİMİ (12 Haz 2026) — KVKK Çerez Bandı
// ──────────────────────────────────────────────────────────────────────────
// KVKK Çerez Rehberi (Haz 2022) hizası:
//   - Zorunlu çerezler için rıza istenmez; bu band YALNIZ analitik içindir.
//   - Reddetmek kabul etmek kadar kolay (eşit ağırlıklı butonlar).
//   - Karar zaman damgasıyla saklanır: RET 6 ayda, KABUL 13 ayda yenilenir
//     (CNIL pratiği). Karar yalnız localStorage'da (cihaz bazlı).
//   - Oturum açan kullanıcı kararı ConsentSheet'teki opsiyonel checkbox'tan
//     verir → bu band ona hiç görünmez; band anonim/karar-vermemiş oturum
//     yüzeyidir. ConsentSheet açıkken `hidden` prop'u ile render edilmez
//     (tek-rıza-yüzeyi kuralı).
//
// localStorage "mlv3.cookieConsent":
//   yeni format: {"decision":"accepted"|"rejected","ts":<ms>}
//   legacy: düz "accepted"/"rejected" string'i (ts'siz → süresiz geçerli
//   sayılmaz; bir kez yeni formata terfi ettirilir).
//
// Analytics init'i bu karara BAĞLI: src/firebase.js yalnız "accepted"
// kararında firebase/analytics'i yükler (rıza denetimi P1-3 düzeltmesi).

import React, { useCallback, useEffect, useState } from "react";
import "./CookieConsentBanner.css";

const STORAGE_KEY = "mlv3.cookieConsent";
const REJECT_TTL_MS = 6 * 30 * 24 * 60 * 60 * 1000;  // ~6 ay
const ACCEPT_TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000; // ~13 ay
const COOKIE_POLICY_URL = "/gizlilik-politikasi";

export function readCookieDecision() {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    // Legacy düz string → ts'siz; yeni formata terfi ettir (şimdiden say)
    if (raw === "accepted" || raw === "rejected") {
      const upgraded = { decision: raw, ts: Date.now() };
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(upgraded)); } catch (_) {}
      return upgraded;
    }
    const parsed = JSON.parse(raw);
    if (parsed?.decision !== "accepted" && parsed?.decision !== "rejected") return null;
    // Süre dolduysa karar geçersiz → yeniden sor
    const ttl = parsed.decision === "rejected" ? REJECT_TTL_MS : ACCEPT_TTL_MS;
    if (typeof parsed.ts === "number" && Date.now() - parsed.ts > ttl) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

// Ölçüldü: üç çağıranın ÜÇÜ de yalnız bu iki değeri geçiyor
// (ConsentSheet.tsx:84 · burada 73 ve 78) → gerçek birleşim tipi bu.
export function writeCookieDecision(decision: "accepted" | "rejected") {
  try {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem(
      STORAGE_KEY,
      JSON.stringify({ decision, ts: Date.now() }),
    );
  } catch (_) {}
}

export default function CookieConsentBanner({ hidden = false }) {
  const [decision, setDecision] = useState(() => readCookieDecision()?.decision ?? null);

  useEffect(() => {
    const v = readCookieDecision()?.decision ?? null;
    if (v !== decision) setDecision(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccept = useCallback(() => {
    writeCookieDecision("accepted");
    setDecision("accepted");
  }, []);

  const handleReject = useCallback(() => {
    writeCookieDecision("rejected");
    setDecision("rejected");
  }, []);

  // Tam ekran rıza modalı açıkken band DOM'a hiç girmez (aria/odak temiz)
  if (hidden || decision != null) return null;

  return (
    <div
      className="mlv3-cookie-consent"
      role="dialog"
      aria-modal="false"
      aria-labelledby="mlv3-cookie-consent-label"
    >
      <p
        id="mlv3-cookie-consent-label"
        className="mlv3-cookie-consent__text"
      >
        Mylasa, deneyimi iyileştirmek için isteğe bağlı analitik çerezler
        kullanır. Reddetsen de uygulama tüm özellikleriyle çalışır.{" "}
        <a
          href={COOKIE_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mlv3-cookie-consent__link"
        >
          Çerez Politikası
        </a>
      </p>
      <div className="mlv3-cookie-consent__actions">
        <button
          type="button"
          className="mlv3-cookie-consent__btn mlv3-cookie-consent__btn--ghost"
          onClick={handleReject}
        >
          Reddet
        </button>
        <button
          type="button"
          className="mlv3-cookie-consent__btn mlv3-cookie-consent__btn--primary"
          onClick={handleAccept}
        >
          Kabul Et
        </button>
      </div>
    </div>
  );
}
